import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import useSession from '../hooks/useSession';
import { enqueueSnackbar } from 'notistack';

export const UserContext = createContext({});
export const UserContextProvider = ({ children, props }) => {
  const [userObject, setUser] = useState(
    localStorage.getItem('DQPN_AUTH') ? JSON.parse(localStorage.getItem('DQPN_AUTH')) : {},
  );

  // Decode the auto auth key from the URL and update local storage and state
  const handleIncomingAuth = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authParam = urlParams.get('auto_auth');

    if (authParam) {
      try {
        // Safely decode from URL and Base64
        const decodedAuth = decodeURIComponent(escape(atob(authParam)));
        const { auth, cookies } = JSON.parse(decodedAuth);

        if (auth) {
          localStorage.setItem('DQPN_AUTH', JSON.stringify(auth));
        }
        if (cookies) {
          localStorage.setItem('cookiePreferences', JSON.stringify(cookies));
        }

        // Clean up URL params after processing
        urlParams.delete('auto_auth');
        const newQueryString = urlParams.toString();
        const newUrl = `${window.location.origin}${newQueryString ? '?' + newQueryString : ''}`;

        window.history.replaceState({}, '', newUrl);
        // Only reload if there was a URL update to remove parameters
        if (window.location.search !== newUrl) {
          window.location.replace(newUrl);
        }

        console.log('Auto-auth handled successfully:', { auth, cookies });
      } catch (error) {
        console.error('Failed to parse auto_auth data:', error);
      }
    }
  }, []);
  useEffect(() => {
    handleIncomingAuth();
  }, [handleIncomingAuth]);

  const { data: sessionObject, refetch } = useSession();
  const user = useMemo(
    () => ({
      ...userObject,
      ...sessionObject,
    }),
    [userObject, sessionObject],
  );
  useEffect(() => {
    if (sessionObject) {
      const updatedUser = {
        ...userObject,
        ...sessionObject,
      };
      setUser(updatedUser);
      localStorage.setItem('DQPN_AUTH', JSON.stringify(updatedUser));
    }
  }, [sessionObject]);

  useEffect(() => {
    if (!userObject?.workflowShareUrl) {
      const timeoutId = setTimeout(() => {
        refetch();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [userObject?.workflowShareUrl, refetch]);

  const logout = useCallback(() => {
    enqueueSnackbar("You've been logged out successfully.", {
      variant: 'info',
      autoHideDuration: 3000,
      onClose: () => {
        localStorage.removeItem('DQPN_AUTH');
        window.location = window.location.protocol + '//' + window.location.host + '/';
      },
    });
  }, []);

  return (
    <UserContext.Provider
      value={{
        actions: {
          setUser,
          logout,
          refetch,
        },
        state: {
          userObject,
          ...user,
        },
      }}
      {...props}
    >
      {children}
    </UserContext.Provider>
  );
};
